import { useQueryClient } from '@tanstack/react-query';
import {
  ResolveAutomationFailureRequestResolution,
  useGetAutomationFailure,
  useReopenAutomationFailure,
  useResolveAutomationFailure,
} from '@urbanx/agx.support.client';
import {
  AgxLoadingState,
  AgxModal,
  AgxNotificationBox,
  useToast,
} from '@urbanx/agx.ui-components2';
import { invalidateAutomationFailureQueries } from './automationFailureCache';
import AutomationFailureDetailActions from './AutomationFailureDetailActions';
import AutomationFailureDetailBody from './AutomationFailureDetailBody';
import AutomationFailureDetailHeader from './AutomationFailureDetailHeader';
import { buildAutomationFailureDiagnostics } from './automationFailurePresentation';

interface AutomationFailureDetailProps {
  reference: string;
  onClose: () => void;
  onFailureChanged: () => void | Promise<void>;
}

const AutomationFailureDetail = ({
  reference,
  onClose,
  onFailureChanged,
}: AutomationFailureDetailProps) => {
  const queryClient = useQueryClient();
  const { pushToast } = useToast();
  const { data: failure, isPending, isError } = useGetAutomationFailure(reference);

  const refreshFailure = async () => {
    await invalidateAutomationFailureQueries(queryClient, reference);
    await onFailureChanged();
  };

  const { resolveAutomationFailure, resolveAutomationFailurePending } = useResolveAutomationFailure(
    {
      onSuccess: async () => {
        await refreshFailure();
        pushToast({ message: 'Automation failure resolved', type: 'success' });
      },
      onError: () => {
        pushToast({ message: 'Error resolving automation failure', type: 'error' });
      },
    }
  );

  const { reopenAutomationFailure, reopenAutomationFailurePending } = useReopenAutomationFailure({
    onSuccess: async () => {
      await refreshFailure();
      pushToast({ message: 'Automation failure reopened', type: 'success' });
    },
    onError: () => {
      pushToast({ message: 'Error reopening automation failure', type: 'error' });
    },
  });

  const resolveFailure = async (resolution: ResolveAutomationFailureRequestResolution) => {
    if (resolveAutomationFailurePending) return;

    await resolveAutomationFailure({ reference, resolution });
  };

  const reopenFailure = async () => {
    await reopenAutomationFailure({ reference });
  };

  const copyDiagnostics = async () => {
    if (!failure) return;

    try {
      await navigator.clipboard.writeText(buildAutomationFailureDiagnostics(failure));
      pushToast({ message: 'Diagnostics copied', type: 'success' });
    } catch {
      pushToast({ message: 'Error copying diagnostics', type: 'error' });
    }
  };

  const content = isPending ? (
    <AgxLoadingState />
  ) : isError || !failure ? (
    <AgxNotificationBox
      type="danger"
      heading="Unable to load automation failure"
      body="Close this window and try again."
    />
  ) : (
    <AutomationFailureDetailBody failure={failure} />
  );

  return (
    <AgxModal
      id="automation-failure-detail"
      type="wide"
      variant="withDivider"
      actionButtonContainerStyle="withDivider"
      minWidth="640px"
      maxWidth="640px"
      secondaryTitle={<AutomationFailureDetailHeader failure={failure} />}
      setIsDialogOpen={onClose}
      content={content}
      actionButtons={
        <AutomationFailureDetailActions
          failure={failure}
          isPending={isPending}
          isError={isError}
          resolvePending={resolveAutomationFailurePending}
          reopenPending={reopenAutomationFailurePending}
          onCopyDiagnostics={copyDiagnostics}
          onResolve={resolveFailure}
          onReopen={reopenFailure}
        />
      }
    />
  );
};

export default AutomationFailureDetail;
